import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SuperHead } from "@/pages/super/SuperLayout";
import { api, formatApiError } from "@/lib/api";

export default function SuperPayments() {
  const [s, setS] = useState(null);
  const [f, setF] = useState({ secret_key: "", publishable_key: "", webhook_secret: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/super/stripe").then((r) => setS(r.data)); }, []);
  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/stripe/webhook`;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.put("/super/stripe", f); setS(data); setF({ secret_key: "", publishable_key: "", webhook_secret: "" }); toast.success("Stripe keys saved"); } catch (e2) { toast.error(formatApiError(e2)); }
    setBusy(false);
  };
  const reset = async () => {
    if (!window.confirm("Remove your Stripe keys and go back to the built-in test account?")) return;
    try { setS((await api.delete("/super/stripe")).data); toast.success("Using the built-in test account"); } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!s) return null;

  return (
    <div className="max-w-3xl space-y-6">
      <SuperHead eyebrow="Settings" title="Payment settings" />
      <div className="rounded-2xl bg-card border border-border p-6 flex items-start gap-4" data-testid="stripe-status">
        <ShieldCheck className="h-6 w-6 text-primary mt-0.5" />
        <div className="text-sm space-y-1">
          {s.source === "custom" ? (
            <>
              <p className="font-semibold">Using your Stripe account ({s.mode === "live" ? "LIVE mode" : "test mode"})</p>
              <p className="text-muted-foreground">Secret key ending •••• {s.secret_last4} · account {s.account_id} · webhook secret {s.has_webhook_secret ? "set" : "not set"}</p>
            </>
          ) : (
            <p className="font-semibold">Using the built-in Stripe test account. Add your own keys below to take payments into your account.</p>
          )}
        </div>
      </div>
      <form onSubmit={save} className="rounded-2xl bg-card border border-border p-6 space-y-4" data-testid="stripe-keys-form">
        <p className="font-display font-bold">{s.source === "custom" ? "Replace Stripe keys" : "Add your Stripe keys"}</p>
        <div className="space-y-1.5"><Label>Secret key</Label><Input type="password" autoComplete="off" placeholder="sk_live_… or sk_test_…" value={f.secret_key} onChange={(e) => setF({ ...f, secret_key: e.target.value })} required data-testid="stripe-secret-key-input" /></div>
        <div className="space-y-1.5"><Label>Publishable key</Label><Input placeholder="pk_live_… or pk_test_…" value={f.publishable_key} onChange={(e) => setF({ ...f, publishable_key: e.target.value })} data-testid="stripe-publishable-key-input" /></div>
        <div className="space-y-1.5"><Label>Webhook signing secret</Label><Input type="password" autoComplete="off" placeholder="whsec_…" value={f.webhook_secret} onChange={(e) => setF({ ...f, webhook_secret: e.target.value })} data-testid="stripe-webhook-secret-input" /></div>
        <div className="rounded-xl bg-muted p-4 text-xs text-slate-600 space-y-1">
          <p>In Stripe, go to Developers → Webhooks and add this endpoint:</p>
          <p className="font-mono break-all text-foreground" data-testid="stripe-webhook-url">{webhookUrl}</p>
          <p>Events: checkout.session.completed, checkout.session.expired, charge.refunded, customer.subscription.updated, customer.subscription.deleted, invoice.paid, invoice.payment_failed. For clinic payments (clinics take payments in their own connected Stripe accounts), also add a second endpoint at the same URL listening to "Connected accounts" with events account.updated, checkout.session.completed and charge.refunded.</p>
          <p>Bookings paid with the old keys can only be refunded through the old Stripe account.</p>
        </div>
        <div className="flex gap-3">
          <Button type="submit" className="rounded-full" disabled={busy || !f.secret_key} data-testid="stripe-save-button">Verify & save</Button>
          {s.source === "custom" && <Button type="button" variant="outline" className="rounded-full" onClick={reset} data-testid="stripe-reset-button">Remove my keys</Button>}
        </div>
      </form>
    </div>
  );
}
